import { forwardRef, useState } from 'react';
import { Provider, useAtomValue, type createStore } from 'jotai';
import { Tab, TabList, Tabs } from '@carbon/react';
import { format0 } from 'utils/number';
import { allDatasetAtoms } from 'pages/map-query/atoms';
import { datasetRegistry } from 'pages/map-query/datasets';
import { DatasetProvider } from 'pages/map-query/dataset-context';
import { ResultsSection } from 'pages/map-query/results-section/results-section';
import { useMapQueryExport } from 'pages/map-query/use-map-query-export';
import { MapQueryExportBanner } from 'pages/map-query/export-status-banner';
import { AREA_DATASET_IDS } from './hooks';
import styles from './area-detail-body.module.scss';

type JotaiStore = ReturnType<typeof createStore>;

/**
 * A dataset's tab, with its total inside the area once the search answers, as
 * Map Query's tabs read. TabList hands each tab a ref for its arrow keys, so this
 * passes it on to the Tab.
 */
const DatasetTab = forwardRef<
  HTMLButtonElement,
  { id: string; store: JotaiStore }
>(({ id, store }, ref) => {
  const { data } = useAtomValue(allDatasetAtoms[id].searchAtom, { store });
  const { label } = datasetRegistry[id];
  const count = data?.totalCount;
  return (
    <Tab ref={ref}>
      {count === undefined ? label : `${label} (${format0(count)})`}
    </Tab>
  );
});
DatasetTab.displayName = 'DatasetTab';

/**
 * The active dataset's table: Map Query's own results section, with its export,
 * reading the area's store.
 *
 * @param {object} props - The dataset to show.
 * @param {string} props.id - The Map Query dataset.
 * @return {JSX.Element} The table and the export's status.
 */
function DatasetResults({ id }: { id: string }): JSX.Element {
  const mapQueryExport = useMapQueryExport();
  const { data, loading, error, currentPayload } = useAtomValue(
    allDatasetAtoms[id].searchAtom
  );
  // Wells says so when the boundary itself catches none, as the page always has.
  // Only then: with a search typed the table stays, or its search box goes too.
  if (
    id === 'wells' &&
    !loading &&
    !error &&
    data?.totalCount === 0 &&
    !currentPayload?.text
  ) {
    return (
      <p className={styles.empty}>
        No wells intersect this area&apos;s boundary.
      </p>
    );
  }
  return (
    <>
      <MapQueryExportBanner mapQueryExport={mapQueryExport} />
      <div className={styles.datasetPanel}>
        <DatasetProvider
          config={datasetRegistry[id]}
          atoms={allDatasetAtoms[id]}
        >
          <ResultsSection mapQueryExport={mapQueryExport} />
        </DatasetProvider>
      </div>
    </>
  );
}

/**
 * Area Scoped Datasets: Map Query's Wells, Documents and Petrotech Maps tables,
 * each searching inside the area. The store comes from the page, which scopes it
 * and shares it with the map, so "Show on map" lands on the page's map.
 *
 * @param {object} props - The area's dataset store.
 * @param {JotaiStore} props.store - From `useAreaDatasetStore`.
 * @return {JSX.Element} The tabs and the active table.
 */
export function AreaScopedDatasets({
  store,
}: {
  store: JotaiStore;
}): JSX.Element {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className={styles.datasetTables}>
      <Tabs
        selectedIndex={activeIndex}
        onChange={({ selectedIndex }) => setActiveIndex(selectedIndex)}
      >
        <TabList aria-label="Area scoped datasets">
          {AREA_DATASET_IDS.map((id) => (
            <DatasetTab key={id} id={id} store={store} />
          ))}
        </TabList>
      </Tabs>
      {/* Only the active table mounts, as in Map Query, so it sizes its columns
          to a real width. */}
      <Provider store={store}>
        <DatasetResults
          key={AREA_DATASET_IDS[activeIndex]}
          id={AREA_DATASET_IDS[activeIndex]}
        />
      </Provider>
    </div>
  );
}
